import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import api from '../services/api';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Heatmap Layer Component
function HeatmapLayer({ data, gradient }) {
  const map = useMap();
  
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    const heatLayer = L.heatLayer(data, {
      radius: 25,
      blur: 35,
      maxZoom: 10,
      max: 1.0,
      gradient: gradient || {
        0.0: 'blue',
        0.5: 'lime',
        0.7: 'yellow',
        1.0: 'red'
      }
    }).addTo(map);
    
    return () => {
      map.removeLayer(heatLayer);
    };
  }, [data, map, gradient]);
  
  return null;
}

const GeoMarketing = () => {
  const [activeLayer, setActiveLayer] = useState('visits');
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarkers, setShowMarkers] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHeatmapData();
  }, []);

  const fetchHeatmapData = async () => {
    try {
      const response = await api.get('/geo-marketing/heatmap-data');
      setHeatmapData(response.data);
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      setError('Failed to load geographic data');
    } finally {
      setLoading(false);
    }
  };

  const getActiveHeatmapData = () => {
    if (!heatmapData || !heatmapData.layers) return [];
    return heatmapData.layers[activeLayer] || [];
  };

  const getLayerGradient = () => {
    const gradients = {
      visits: { 0.0: 'blue', 0.5: 'cyan', 0.7: 'yellow', 1.0: 'red' },
      contacts: { 0.0: 'green', 0.5: 'lime', 0.7: 'yellow', 1.0: 'orange' },
      leads: { 0.0: 'purple', 0.5: 'blue', 0.7: 'orange', 1.0: 'red' }
    };
    return gradients[activeLayer];
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Geo Marketing
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🗺️ Geo Marketing
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Location-based analytics with interactive heatmaps
            </p>
          </div>
        </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--red-50)',
          border: '1px solid var(--red-200)',
          color: 'var(--red-800)',
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Summary Stats */}
      {heatmapData && heatmapData.summary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            textAlign: 'center',
            padding: '1rem',
            border: '1px solid var(--gray-200)',
            borderRadius: '8px',
            backgroundColor: 'var(--gray-50)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👁️</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
              {heatmapData.summary.total_visits.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Visits</div>
          </div>
          
          <div style={{ 
            textAlign: 'center',
            padding: '1rem',
            border: '1px solid var(--gray-200)',
            borderRadius: '8px',
            backgroundColor: 'var(--gray-50)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📧</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--green-600)' }}>
              {heatmapData.summary.total_contacts.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Contacts</div>
          </div>
          
          <div style={{ 
            textAlign: 'center',
            padding: '1rem',
            border: '1px solid var(--gray-200)',
            borderRadius: '8px',
            backgroundColor: 'var(--gray-50)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--orange-600)' }}>
              {heatmapData.summary.total_leads.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Leads</div>
          </div>
          
          <div style={{ 
            textAlign: 'center',
            padding: '1rem',
            border: '1px solid var(--gray-200)',
            borderRadius: '8px',
            backgroundColor: 'var(--gray-50)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📍</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--purple-600)' }}>
              {heatmapData.summary.locations_tracked}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Locations</div>
          </div>
        </div>
      )}

      {/* Map Controls */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setActiveLayer('visits')}
              style={{
                padding: '0.5rem 1rem',
                border: activeLayer === 'visits' ? '2px solid var(--primary-600)' : '1px solid var(--gray-300)',
                backgroundColor: activeLayer === 'visits' ? 'var(--primary-50)' : 'white',
                color: activeLayer === 'visits' ? 'var(--primary-600)' : 'var(--gray-700)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem'
              }}
            >
              👁️ Web Visits
            </button>
            <button
              onClick={() => setActiveLayer('contacts')}
              style={{
                padding: '0.5rem 1rem',
                border: activeLayer === 'contacts' ? '2px solid var(--green-600)' : '1px solid var(--gray-300)',
                backgroundColor: activeLayer === 'contacts' ? 'var(--green-50)' : 'white',
                color: activeLayer === 'contacts' ? 'var(--green-600)' : 'var(--gray-700)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem'
              }}
            >
              📧 Contacts
            </button>
            <button
              onClick={() => setActiveLayer('leads')}
              style={{
                padding: '0.5rem 1rem',
                border: activeLayer === 'leads' ? '2px solid var(--orange-600)' : '1px solid var(--gray-300)',
                backgroundColor: activeLayer === 'leads' ? 'var(--orange-50)' : 'white',
                color: activeLayer === 'leads' ? 'var(--orange-600)' : 'var(--gray-700)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem'
              }}
            >
              🎯 Leads
            </button>
          </div>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showMarkers}
              onChange={(e) => setShowMarkers(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>Show City Markers</span>
          </label>
        </div>
      </div>

      {/* Map */}
      <div style={{ padding: 0, overflow: 'hidden', height: '600px', marginBottom: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
        <MapContainer
          center={[39.8283, -98.5795]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <HeatmapLayer 
            data={getActiveHeatmapData()} 
            gradient={getLayerGradient()}
          />
          
          {showMarkers && heatmapData && heatmapData.markers && heatmapData.markers.map((city, idx) => (
            <Marker key={idx} position={[city.lat, city.lng]}>
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 'bold' }}>
                    {city.name}
                  </h3>
                  <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                    <div><strong>Visits:</strong> {city.visits.toLocaleString()}</div>
                    <div><strong>Contacts:</strong> {city.contacts.toLocaleString()}</div>
                    <div><strong>Leads:</strong> {city.leads.toLocaleString()}</div>
                    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #ddd' }}>
                      <strong>Conversion:</strong> {((city.leads / city.visits) * 100).toFixed(2)}%
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div style={{ marginTop: '1rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>Heatmap Legend</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Low Activity</span>
          <div style={{
            flex: 1,
            height: '20px',
            background: activeLayer === 'visits' 
              ? 'linear-gradient(to right, blue, cyan, yellow, red)'
              : activeLayer === 'contacts'
              ? 'linear-gradient(to right, green, lime, yellow, orange)'
              : 'linear-gradient(to right, purple, blue, orange, red)',
            borderRadius: '4px'
          }}></div>
          <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>High Activity</span>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GeoMarketing;
